import { Command } from 'commander'
import { createApiClient } from '../http.js'
import { account } from '../api/catalog.js'
import { call, pageItems, type PagedResult } from '../api/client.js'
import { resolveAccountId } from '../context.js'
import { output, outputRecord } from '../output.js'

interface InvitationDto {
  id: string
  emailAddress: string
  roleKey: string
  status: string
  expiresAt?: string
  [key: string]: unknown
}

export function registerAccount(program: Command): void {
  const accountCmd = program.command('account').description('Manage Verentis accounts')

  accountCmd
    .command('get')
    .description('Show account details')
    .option('--account <accountId>', 'account id (default: configured/derived account)')
    .option('--json', 'output JSON')
    .action(async (options: { account?: string; json?: boolean }) => {
      const accountId = (await resolveAccountId(options))!
      const api = await createApiClient()
      const result = await call<Record<string, unknown>>(api, account.get, { params: { accountId } })
      outputRecord(result, options)
    })

  accountCmd
    .command('update')
    .description('Update account details')
    .requiredOption('--name <name>', 'account name')
    .option('--description <description>')
    .option('--account <accountId>', 'account id')
    .action(async (options: { name: string; description?: string; account?: string }) => {
      const accountId = (await resolveAccountId(options))!
      const api = await createApiClient()
      await call(api, account.update, {
        params: { accountId },
        body: { accountId, name: options.name, description: options.description ?? null },
      })
      console.log(`Account ${accountId} updated.`)
    })

  const inviteCmd = accountCmd.command('invite').description('Manage account invitations')

  inviteCmd
    .command('list')
    .description('List invitations for the account')
    .option('--account <accountId>', 'account id')
    .option('--json', 'output JSON')
    .action(async (options: { account?: string; json?: boolean }) => {
      const accountId = (await resolveAccountId(options))!
      const api = await createApiClient()
      const page = await call<PagedResult<InvitationDto>>(api, account.listInvitations, { params: { accountId } })
      const items = pageItems(page)
      output(items, { emailAddress: 'Email', roleKey: 'Role', status: 'Status', id: 'Id' }, options, page)
    })

  inviteCmd
    .command('create <email>')
    .description('Invite a user to the account')
    .option('--role <roleKey>', 'role to assign on acceptance', 'member')
    .option('--account <accountId>', 'account id')
    .action(async (email: string, options: { role: string; account?: string }) => {
      const accountId = (await resolveAccountId(options))!
      const api = await createApiClient()
      const id = await call<string>(api, account.createInvitation, {
        params: { accountId },
        body: { accountId, emailAddress: email, roleKey: options.role },
      })
      console.log(`Invitation ${id ?? ''} sent to ${email} (role ${options.role}).`.replace('  ', ' '))
    })

  inviteCmd
    .command('resend <invitationId>')
    .description('Resend an invitation email')
    .option('--account <accountId>', 'account id')
    .action(async (invitationId: string, options: { account?: string }) => {
      const accountId = (await resolveAccountId(options))!
      const api = await createApiClient()
      await call(api, account.resendInvitation, { params: { accountId, invitationId }, body: { accountId, invitationId } })
      console.log(`Invitation ${invitationId} resent.`)
    })

  inviteCmd
    .command('revoke <invitationId>')
    .description('Revoke an invitation')
    .option('--account <accountId>', 'account id')
    .action(async (invitationId: string, options: { account?: string }) => {
      const accountId = (await resolveAccountId(options))!
      const api = await createApiClient()
      await call(api, account.revokeInvitation, { params: { accountId, invitationId } })
      console.log(`Invitation ${invitationId} revoked.`)
    })

  inviteCmd
    .command('accept <code>')
    .description('Accept an account invitation code as the current user')
    .action(async (code: string) => {
      const api = await createApiClient()
      await call(api, account.acceptInvitation, { body: { code } })
      console.log('Invitation accepted.')
    })
}
